import { NgClass } from '@angular/common';
import { AfterViewInit, Component, DOCUMENT, OnDestroy, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet, isActive } from '@angular/router';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { LoadingComponent } from '@shared/components/loading/loading.component';
import { ModalTemplateDirective } from '@shared/components/modal/modal-template.directive';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { RefreshNowAppBannerComponent } from '@shared/components/refresh-now-app-banner/refresh-now-app-banner.component';
import { ToastComponent } from '@shared/components/toast/toast.component';
import { MaintenancePageComponent } from '@shared/pages/maintenance-page/maintenance-page.component';
import { clientIds } from '@shared/constants';
import { appName } from '@shared/constants/app-name';
import { ClientApps } from '@shared/models/enums/apps.enum';
import { AuthorizeService, EventBusService, UtilsService } from '@shared/services';
import { MaintenanceService } from '@shared/services/maintenance.service';
import { ScriptVersionService } from '@shared/services/script-version.service';
import { UserpilotService } from '@shared/services/userpilot.service';
import { Subscription } from 'rxjs';
import { environment } from '../environments/environment';
import { HeaderComponent } from './components';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-unsafe-function-type
    Genesys: Function;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    LoadingComponent,
    ModalTemplateDirective,
    ModalComponent,
    ToastComponent,
    HeaderComponent,
    NgClass,
    RefreshNowAppBannerComponent,
    RouterOutlet,
    FooterComponent,
    MaintenancePageComponent,
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly authService = inject(AuthorizeService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly utilService = inject(UtilsService);
  public readonly scriptVersionService = inject(ScriptVersionService);
  public readonly maintenanceService = inject(MaintenanceService);
  private readonly eventBusService = inject(EventBusService);
  private readonly userpilotService = inject(UserpilotService);
  private readonly ngbTooltipConfig = inject(NgbTooltipConfig);

  public refreshNowAppBannerText = 'We have made some updates to Ignite.';
  public isAppHeaderHidden = false;

  private resultsMaximizedSubscription: Subscription | undefined;

  public ngOnInit() {
    (window as any).appName = appName.clubHub;
    this.router.events.subscribe(event => {
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });

    this.resultsMaximizedSubscription = this.eventBusService.maximizeProductsResultsChanged$.subscribe(isProductsResultsMaximized => {
      this.isAppHeaderHidden = isProductsResultsMaximized;
    });

    this.userpilotService.run();
    this.setTitle();

    this.ngbTooltipConfig.animation = false;
  }

  public ngOnDestroy(): void {
    this.userpilotService.stop();
    this.resultsMaximizedSubscription?.unsubscribe();
  }

  public get isAffordability() {
    return isActive('/ignite/affordability', this.router, {
      paths: 'subset',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  public get isProducts() {
    return isActive('/ignite/products', this.router, {
      paths: 'subset',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  public get isCriteriaOutcomePage() {
    return /^\/criteria\/landing\/\d\/issues/.test(this.router.url);
  }

  public ngAfterViewInit() {
    this.setupGenesys();
  }

  private setupGenesys() {
    setTimeout(() => {
      this.authService.getUser().subscribe(user => {
        // SimplyBiz do not get the Genesys chat window
        if (user?.sso_id === clientIds.simplyBiz) {
          return;
        }

        const script = this.document.createElement('script');
        const src = 'assets/js/genesys.js';
        script.type = 'text/javascript';
        script.src = src;
        if (!this.utilService.scriptExists(src)) {
          this.document.getElementsByTagName('head')[0].appendChild(script);
        }
      });
    }, 2000);
  }

  private setTitle() {
    this.authService.getUser().subscribe(() => {
      const title = environment.sharedConfig.applicationName === ClientApps.Ignite
        ? ClientApps.Ignite
        : ClientApps.ClubHub;
      this.title.setTitle(title);
    });
  }
}
